"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.perspectivesContract = exports.tooManyWriteInPerspectivesErrorSpec = exports.perspectiveTextTooLongErrorSpec = exports.perspectiveTextInvalidCharactersErrorSpec = exports.perspectiveTextHasEmojiErrorSpec = exports.perspectiveSchema = exports.perspectiveInputSchema = exports.MAX_PERSPECTIVE_EMOJI_LENGTH = exports.MAX_PERSPECTIVES = void 0;
exports.errorDuplicatePerspective = errorDuplicatePerspective;
exports.errorPerspectiveTextHasEmoji = errorPerspectiveTextHasEmoji;
exports.errorPerspectiveTextInvalidCharacters = errorPerspectiveTextInvalidCharacters;
exports.errorWriteInPerspectiveHasEmoji = errorWriteInPerspectiveHasEmoji;
exports.errorWriteInPerspectiveNoLetter = errorWriteInPerspectiveNoLetter;
exports.errorWriteInPerspectiveTooLong = errorWriteInPerspectiveTooLong;
exports.errorTooManyWriteInPerspectives = errorTooManyWriteInPerspectives;
/**
 * Perspectives: contract definitions for a space's perspective-list
 * sub-resource.
 *
 * A space running the Representing Perspectives format offers readers a curated
 * set of viewpoints to tag their comment with. Nested under `spaces` as
 * `spaces.perspectives` (see `contract.ts`) — a sub-resource rather than a
 * `Space` field, since only one format uses it.
 *
 * The wire carries a structured list in both directions: each entry is its
 * display label plus an optional emoji. How the list is stored — one
 * newline-separated string, emoji first — never crosses the wire, so no caller
 * parses or assembles it. `perspectiveKey` is derived server-side and returned
 * but never accepted, so a client cannot disagree with the server about the key
 * a comment's tag has to match.
 *
 * The read is public: the composer and poll display serve signed-out visitors.
 * The write is editorial, gated as `spaces.create` and `spaces.update` are.
 */
const client_1 = require("@orpc/client");
const z = __importStar(require("zod"));
const roles_capabilities_1 = require("../../roles-capabilities");
const api_schemas_1 = require("../api-schemas");
const base_1 = require("../base");
/**
 * Upper bound on how many perspectives one space can offer. The editorial
 * guidance asks for 3–5; this is a generous ceiling, not a product limit.
 */
exports.MAX_PERSPECTIVES = 50;
/** Enough for any single grapheme cluster, including ZWJ emoji sequences. */
exports.MAX_PERSPECTIVE_EMOJI_LENGTH = 32;
/** One entry as written: the label, plus the emoji shown beside it. */
exports.perspectiveInputSchema = z.object({
    text: api_schemas_1.shortString.describe('The display label. Must not begin with an emoji — pass that as `emoji`.'),
    emoji: z
        .string()
        .max(exports.MAX_PERSPECTIVE_EMOJI_LENGTH)
        .nullable()
        .optional()
        .describe('An emoji shown beside the label, or null/omitted for none.'),
});
/** One entry as read. The same fields, plus the server-derived key. */
exports.perspectiveSchema = z.object({
    perspectiveKey: z.string().describe("The entry's derived key (stringToFbKey(text)) — what a comment's tag is keyed by."),
    text: z.string().describe('The display label.'),
    emoji: z.string().nullable().describe('The emoji shown beside the label, or null when there is none.'),
});
const perspectivesOutput = z.object({
    perspectives: z.array(exports.perspectiveSchema).describe("The space's perspectives, in the order a reader sees them."),
});
const get = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/spaces/{spaceKey}/perspectives',
    tags: ['Spaces'],
    summary: "Get a space's perspective list",
    description: 'List the predefined perspectives a commenter might identify by, used when a conversation space is using the ' +
        'Representing Perspectives format. A comment can carry more than one. Each entry gives its label, the emoji shown beside ' +
        "it, and the server-derived `perspectiveKey` a comment's tag has to match. A space without defined perspectives returns " +
        'an empty list.',
})
    .meta({ access: 'public' })
    .input(z.object({ ...api_schemas_1.spaceReadParams }))
    .output(perspectivesOutput);
/**
 * A perspective label with an emoji in it. A predefined perspective may not
 * begin with one, and a reader's write-in may not contain one anywhere.
 */
exports.perspectiveTextHasEmojiErrorSpec = { status: 400, data: z.object({ text: z.string() }) };
/**
 * A perspective label with no letter in it, such as digits or punctuation
 * alone.
 */
exports.perspectiveTextInvalidCharactersErrorSpec = { status: 400, data: z.object({ text: z.string() }) };
/**
 * A reader's write-in perspective longer than `maxLength` characters once
 * trimmed.
 */
exports.perspectiveTextTooLongErrorSpec = { status: 400, data: z.object({ text: z.string(), maxLength: z.number() }) };
/** A comment that carries more than one write-in perspective. */
exports.tooManyWriteInPerspectivesErrorSpec = { status: 400, data: z.object({ count: z.number() }) };
const set = base_1.baseProcedure
    .route({
    method: 'PUT',
    path: '/silo/{siloKey}/spaces/{spaceKey}/perspectives',
    tags: ['Spaces'],
    summary: "Replace a space's perspective list",
    description: 'Replace the predefined perspectives a commenter might identify by, used when a conversation space is using the ' +
        'Representing Perspectives format, and return the new list. The whole list is replaced, in the order given, and an empty ' +
        'array clears it. Two entries whose labels derive the same key are refused with DUPLICATE_PERSPECTIVE, since a comment ' +
        'tagged with one could not be told from the other. A label that begins with an emoji is refused with ' +
        'PERSPECTIVE_TEXT_HAS_EMOJI; pass the emoji as `emoji` instead. A label that contains no letter or emoji is refused ' +
        'with PERSPECTIVE_TEXT_INVALID_CHARACTERS.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.EDITORIALDASH_MANAGE_CAPABILITY })
    .input(z.object({ ...api_schemas_1.spaceParams, perspectives: z.array(exports.perspectiveInputSchema).max(exports.MAX_PERSPECTIVES) }))
    .output(perspectivesOutput)
    .errors({
    DUPLICATE_PERSPECTIVE: { status: 400, data: z.object({ perspectiveKey: z.string() }) },
    PERSPECTIVE_TEXT_HAS_EMOJI: exports.perspectiveTextHasEmojiErrorSpec,
    PERSPECTIVE_TEXT_INVALID_CHARACTERS: exports.perspectiveTextInvalidCharactersErrorSpec,
});
/**
 * Thrown when two entries derive the same `perspectiveKey`. A comment tags
 * itself by that key, so duplicates would merge in the poll tally and
 * collide as list keys.
 */
function errorDuplicatePerspective(perspectiveKey) {
    return new client_1.ORPCError('DUPLICATE_PERSPECTIVE', {
        status: 400,
        data: { perspectiveKey },
        message: 'Two perspectives have the same label: ' + perspectiveKey,
    });
}
/**
 * Thrown when an entry's `text` begins with an emoji. Storage puts the emoji
 * first on the line, so such a label would read back with that emoji split off
 * into `emoji` — a write that would not survive its own round trip.
 */
function errorPerspectiveTextHasEmoji(text) {
    return new client_1.ORPCError('PERSPECTIVE_TEXT_HAS_EMOJI', {
        status: 400,
        data: { text },
        message: 'Perspective text must not begin with an emoji; pass it as `emoji` instead: ' + text,
    });
}
/**
 * Thrown when an entry's `text` contains no letter or emoji — for example a
 * label made entirely of digits, punctuation, or symbols.
 */
function errorPerspectiveTextInvalidCharacters(text) {
    return new client_1.ORPCError('PERSPECTIVE_TEXT_INVALID_CHARACTERS', {
        status: 400,
        data: { text },
        message: 'Perspective text must contain at least one letter or emoji: ' + text,
    });
}
/** Thrown when a reader's write-in perspective contains an emoji. */
function errorWriteInPerspectiveHasEmoji(text) {
    return new client_1.ORPCError('PERSPECTIVE_TEXT_HAS_EMOJI', {
        status: 400,
        data: { text },
        message: 'Emoji are not allowed in write-in perspectives',
    });
}
/** Thrown when a reader's write-in perspective contains no letter. */
function errorWriteInPerspectiveNoLetter(text) {
    return new client_1.ORPCError('PERSPECTIVE_TEXT_INVALID_CHARACTERS', {
        status: 400,
        data: { text },
        message: 'Write-in perspective must contain at least one letter',
    });
}
/**
 * Thrown when a reader's write-in perspective is longer than `maxLength` once
 * trimmed.
 */
function errorWriteInPerspectiveTooLong(text, maxLength) {
    return new client_1.ORPCError('PERSPECTIVE_TEXT_TOO_LONG', {
        status: 400,
        data: { text, maxLength },
        message: `Write-in perspective must be 1\u2013${maxLength} allowed characters`,
    });
}
/** Thrown when a comment carries more than one write-in perspective. */
function errorTooManyWriteInPerspectives(count) {
    return new client_1.ORPCError('TOO_MANY_WRITE_IN_PERSPECTIVES', {
        status: 400,
        data: { count },
        message: `A comment can carry at most one write-in perspective, not ${count}`,
    });
}
exports.perspectivesContract = { get, set };
//# sourceMappingURL=perspectives.js.map